import { MarketPageAudit } from '../../../common/hooks/use-market-api.hook';

/**
 * The page audit, read like a site audit: each failed check with how much it
 * matters, what area it is in, and — for the ones we can explain — why it
 * matters and how to fix it (copy under BUSINESS.HEALTH.CHECK.<NAME>).
 */

export type Severity = 'error' | 'warning' | 'notice';
export type Theme = 'content' | 'speed' | 'technical' | 'images' | 'links';

export const SEVERITIES: Severity[] = ['error', 'warning', 'notice'];

/**
 * Checks DataForSEO reports as `true` when the page PASSES.
 *
 * The API keeps every check that came back `true` as "failed", which is right
 * for `no_h1_tag` and backwards for these: a page on HTTPS arrived with
 * `is_https` in its failures. They are read as passes here.
 */
export const PASSING_WHEN_TRUE = new Set([
    'is_https',
    'is_www',
    'has_html_doctype',
    'canonical',
    'seo_friendly_url',
    'seo_friendly_url_characters_check',
    'seo_friendly_url_dynamic_check',
    'seo_friendly_url_keywords_check',
    'seo_friendly_url_relative_length_check',
    'has_micromarkup',
    'meta_charset_consistency',
]);

interface CheckInfo {
    severity: Severity;
    theme: Theme;
    /** Has its own "why it matters / how to fix" copy; otherwise the severity's generic copy is used. */
    detailed: boolean;
}

const c = (severity: Severity, theme: Theme, detailed = true): CheckInfo => ({ severity, theme, detailed });

export const CHECKS: Record<string, CheckInfo> = {
    // Errors: search cannot read the page properly, or visitors leave before it loads.
    is_http: c('error', 'technical'),
    is_4xx_code: c('error', 'technical'),
    is_5xx_code: c('error', 'technical'),
    is_broken: c('error', 'technical'),
    no_title: c('error', 'content'),
    high_loading_time: c('error', 'speed'),
    size_greater_than_3mb: c('error', 'speed'),
    canonical_to_broken: c('error', 'technical', false),
    recursive_canonical: c('error', 'technical', false),
    lorem_ipsum: c('error', 'content', false),

    // Warnings: the page is found, but shown worse than it could be.
    no_h1_tag: c('warning', 'content'),
    no_description: c('warning', 'content'),
    title_too_long: c('warning', 'content'),
    title_too_short: c('warning', 'content'),
    duplicate_title_tag: c('warning', 'content'),
    irrelevant_title: c('warning', 'content'),
    irrelevant_description: c('warning', 'content'),
    low_content_rate: c('warning', 'content'),
    low_character_count: c('warning', 'content'),
    no_image_alt: c('warning', 'images'),
    high_waiting_time: c('warning', 'speed'),
    has_render_blocking_resources: c('warning', 'speed'),
    large_page_size: c('warning', 'speed'),
    no_content_encoding: c('warning', 'speed'),
    https_to_http_links: c('warning', 'links'),
    redirect_chain: c('warning', 'technical'),
    duplicate_meta_tags: c('warning', 'technical'),
    no_doctype: c('warning', 'technical'),
    has_micromarkup_errors: c('warning', 'technical'),
    has_meta_refresh_redirect: c('warning', 'technical', false),
    canonical_to_redirect: c('warning', 'technical', false),
    canonical_chain: c('warning', 'technical', false),
    frame: c('warning', 'technical', false),
    has_frame: c('warning', 'technical', false),
    flash: c('warning', 'technical', false),

    // Notices: worth tidying; rarely what holds a page back.
    no_favicon: c('notice', 'technical'),
    no_image_title: c('notice', 'images'),
    low_readability_rate: c('notice', 'content'),
    deprecated_html_tags: c('notice', 'technical'),
    high_content_rate: c('notice', 'content', false),
    high_character_count: c('notice', 'content', false),
    small_page_size: c('notice', 'content', false),
    no_encoding_meta_tag: c('notice', 'technical', false),
    has_links_to_redirects: c('notice', 'links', false),
    is_redirect: c('notice', 'technical', false),
    // Virtues that failed (API checksVersion 2 lists them in `failed`).
    is_https: c('error', 'technical', true),
    has_html_doctype: c('warning', 'technical', true),
    canonical: c('warning', 'technical', true),
    has_micromarkup: c('notice', 'content', true),
    seo_friendly_url: c('notice', 'technical', false),
    meta_charset_consistency: c('notice', 'technical', false),
    is_orphan_page: c('notice', 'links', false),
    irrelevant_meta_keywords: c('notice', 'content', false),
    has_misspelling: c('notice', 'content', false),
};

export interface AuditIssue {
    check: string;
    severity: Severity;
    theme: Theme;
    detailed: boolean;
}

/** The failed checks as issues, most serious first; the misreported passes set aside. */
export function classifyAudit(audit: MarketPageAudit | null | undefined): {
    issues: AuditIssue[];
    passed: string[];
    counts: Record<Severity, number>;
} {
    const issues: AuditIssue[] = [];
    // From checksVersion 2 the API sorts virtues from failures itself, so a
    // virtue in `failed` really failed (no HTTPS). Before it, `failed` held
    // every true check and the virtues have to be picked back out here.
    const sorted = (audit?.checksVersion ?? 0) >= 2;
    const passed: string[] = sorted ? [...(audit?.passed ?? [])] : [];
    for (const check of audit?.failed ?? []) {
        if (!sorted && PASSING_WHEN_TRUE.has(check)) {
            passed.push(check);
            continue;
        }
        // An unknown check is still a failure worth naming, as a notice.
        const info = CHECKS[check] ?? c('notice', 'technical', false);
        issues.push({ check, ...info });
    }
    const order = (s: Severity) => SEVERITIES.indexOf(s);
    issues.sort((a, b) => order(a.severity) - order(b.severity) || a.theme.localeCompare(b.theme));
    const counts = { error: 0, warning: 0, notice: 0 } as Record<Severity, number>;
    for (const i of issues) counts[i.severity]++;
    return { issues, passed, counts };
}

/** A raw check name, readable: 'no_image_alt' → 'No image alt'. For checks we have no words for. */
export const humaniseCheck = (check: string): string => {
    const s = check.replace(/_/g, ' ').trim();
    return s.charAt(0).toUpperCase() + s.slice(1);
};

export type VitalKey = 'largestPaint' | 'interactive' | 'domComplete' | 'layoutShift';
export type VitalLevel = 'good' | 'watch' | 'bad';

/**
 * Google's own thresholds where it publishes them (largest paint 2.5 s / 4 s,
 * layout shift 0.1 / 0.25, interactive 3.8 s / 7.3 s); a fully built page
 * past three seconds is read the same way.
 */
export const VITAL_THRESHOLDS: Record<VitalKey, [number, number]> = {
    largestPaint: [2500, 4000],
    interactive: [3800, 7300],
    domComplete: [3000, 6000],
    layoutShift: [0.1, 0.25],
};

export function vitalLevel(key: VitalKey, value: number | null | undefined): VitalLevel | null {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return null;
    // A zero time was not measured; a zero layout shift is perfect.
    if (value === 0 && key !== 'layoutShift') return null;
    const [good, poor] = VITAL_THRESHOLDS[key];
    return value <= good ? 'good' : value <= poor ? 'watch' : 'bad';
}

export type SnippetLevel = 'missing' | 'short' | 'good' | 'long';

/**
 * How a title or description fits a results page: Google shows roughly 60
 * characters of a title and 160 of a description before cutting them off.
 */
export function snippetFit(text: string | null | undefined, kind: 'title' | 'description'): { length: number; level: SnippetLevel } {
    const length = (text ?? '').trim().length;
    if (!length) return { length, level: 'missing' };
    const [min, max] = kind === 'title' ? [30, 60] : [70, 160];
    return { length, level: length < min ? 'short' : length > max ? 'long' : 'good' };
}
